// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Eye, Edit, Search, Plus, ArrowUpDown, Building2, Trash2 } from 'lucide-react';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';

interface TipoCliente {
  id: string;
  nome: string;
  descricao?: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export default function TipoClientes() {
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const [tipos, setTipos] = useState<TipoCliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<string>('nome');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newTipo, setNewTipo] = useState({
    nome: '',
    descricao: '',
    ativo: true
  });

  useEffect(() => {
    if (hasPermission('clientes', 'tipo_clientes', 'read')) {
      fetchTipos();
    }
  }, [hasPermission, sortBy, sortOrder]);

  const fetchTipos = async () => {
    try {
      let query = supabase
        .from('tipo_clientes')
        .select('*');

      // Aplicar busca
      if (search) {
        query = query.or(`nome.ilike.%${search}%,descricao.ilike.%${search}%`);
      }

      // Aplicar ordenação
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      const { data, error } = await query;

      if (error) throw error;
      setTipos(data || []);
    } catch (error: any) {
      console.error('Erro ao buscar tipos de cliente:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os tipos de cliente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleSearch = () => {
    fetchTipos();
  };

  const handleCreate = async () => {
    try {
      if (!newTipo.nome.trim()) {
        toast({
          title: "Erro",
          description: "O nome é obrigatório.",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('tipo_clientes')
        .insert([{
          nome: newTipo.nome.trim(),
          descricao: newTipo.descricao.trim() || null,
          ativo: newTipo.ativo,
          created_by: (await supabase.auth.getUser()).data.user?.id
        }]);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: `Tipo de cliente "${newTipo.nome}" criado com sucesso.`,
      });

      // Limpar formulário e fechar modal
      setNewTipo({ nome: '', descricao: '', ativo: true });
      setIsCreateModalOpen(false);
      
      // Atualizar a lista
      fetchTipos();
    } catch (error: any) {
      console.error('Erro ao criar tipo de cliente:', error);
      toast({
        title: "Erro",
        description: "Não foi possível criar o tipo de cliente.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string, nome: string) => {
    try {
      const { error } = await supabase
        .from('tipo_clientes')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: `Tipo de cliente "${nome}" excluído com sucesso.`,
      });

      // Atualizar a lista
      fetchTipos();
    } catch (error: any) {
      console.error('Erro ao excluir tipo de cliente:', error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o tipo de cliente.",
        variant: "destructive",
      });
    }
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    return <ArrowUpDown className={`w-4 h-4 ${sortOrder === 'asc' ? 'rotate-180' : ''}`} />;
  };

  if (!hasPermission('clientes', 'tipo_clientes', 'read')) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Você não tem permissão para acessar os tipos de cliente.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Building2 className="w-6 h-6" />
          <h1 className="text-2xl font-bold">Tipos de Cliente</h1>
        </div>
        {hasPermission('clientes', 'tipo_clientes', 'write') && (
          <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Novo Tipo
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Criar Novo Tipo de Cliente</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome *</Label>
                  <Input
                    id="nome"
                    placeholder="Digite o nome do tipo de cliente"
                    value={newTipo.nome}
                    onChange={(e) => setNewTipo({ ...newTipo, nome: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="descricao">Descrição</Label>
                  <Textarea
                    id="descricao"
                    placeholder="Digite uma descrição (opcional)"
                    value={newTipo.descricao}
                    onChange={(e) => setNewTipo({ ...newTipo, descricao: e.target.value })}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="ativo"
                    checked={newTipo.ativo}
                    onCheckedChange={(checked) => setNewTipo({ ...newTipo, ativo: checked })}
                  />
                  <Label htmlFor="ativo">Ativo</Label>
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setNewTipo({ nome: '', descricao: '', ativo: true });
                      setIsCreateModalOpen(false);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button onClick={handleCreate}>
                    Criar Tipo
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros e Busca</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou descrição..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
              />
            </div>
            <Button onClick={handleSearch}>
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Tipos de Cliente</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Carregando tipos de cliente...</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => handleSort('nome')}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Nome</span>
                        {getSortIcon('nome')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => handleSort('descricao')}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Descrição</span>
                        {getSortIcon('descricao')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => handleSort('ativo')}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Status</span>
                        {getSortIcon('ativo')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Criado em</span>
                        {getSortIcon('created_at')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => handleSort('updated_at')}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Atualizado em</span>
                        {getSortIcon('updated_at')}
                      </div>
                    </TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tipos.map((tipo) => (
                    <TableRow key={tipo.id}>
                      <TableCell className="font-medium">{tipo.nome}</TableCell>
                      <TableCell>{tipo.descricao || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={tipo.ativo ? 'default' : 'secondary'}>
                          {tipo.ativo ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {format(new Date(tipo.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                      </TableCell>
                      <TableCell>
                        {format(new Date(tipo.updated_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Detalhes do Tipo de Cliente</AlertDialogTitle>
                              </AlertDialogHeader>
                              <ScrollArea className="max-h-96">
                                <div className="space-y-4">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <h4 className="font-semibold mb-2">Informações Básicas</h4>
                                      <div className="space-y-2 text-sm">
                                        <div>
                                          <span className="font-medium">Nome:</span>
                                          <p className="text-muted-foreground">{tipo.nome}</p>
                                        </div>
                                        <div>
                                          <span className="font-medium">Descrição:</span>
                                          <p className="text-muted-foreground">{tipo.descricao || 'Não informado'}</p>
                                        </div>
                                        <div>
                                          <span className="font-medium">Status:</span>
                                          <p className="text-muted-foreground">
                                            <Badge variant={tipo.ativo ? 'default' : 'secondary'}>
                                              {tipo.ativo ? 'Ativo' : 'Inativo'}
                                            </Badge>
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    <div>
                                      <h4 className="font-semibold mb-2">Auditoria</h4>
                                      <div className="space-y-2 text-sm">
                                        <div>
                                          <span className="font-medium">Criado em:</span>
                                          <p className="text-muted-foreground">
                                            {format(new Date(tipo.created_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                                          </p>
                                        </div>
                                        <div>
                                          <span className="font-medium">Atualizado em:</span>
                                          <p className="text-muted-foreground">
                                            {format(new Date(tipo.updated_at), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                                          </p>
                                        </div>
                                        <div>
                                          <span className="font-medium">ID:</span>
                                          <p className="text-muted-foreground text-xs font-mono">{tipo.id}</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </ScrollArea>
                            </AlertDialogContent>
                          </AlertDialog>
                          
                          {hasPermission('clientes', 'tipo_clientes', 'write') && (
                            <Button variant="outline" size="sm">
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}

                          {hasPermission('clientes', 'tipo_clientes', 'admin') && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Tem certeza que deseja excluir o tipo de cliente "<strong>{tipo.nome}</strong>"?
                                    Esta ação não pode ser desfeita.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(tipo.id, tipo.nome)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {tipos.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Building2 className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">Nenhum tipo de cliente encontrado</p>
                  <p className="text-sm">
                    {search ? 'Tente ajustar os filtros de busca.' : 'Clique em "Novo Tipo" para adicionar o primeiro tipo de cliente.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}